/**
 * InactivityWarningModal
 *
 * Shown at the 14-minute idle mark with a 60-second countdown. The user
 * can either stay signed in (resets the activity timer) or sign out now.
 * If they do nothing, useSessionManager fires the auto-logout when the
 * countdown hits zero.
 *
 * Styling follows the rest of the app: emerald primary, rounded-xl,
 * Lucide icons, Tailwind utility classes only.
 */

import React from 'react';
import { Clock, LogOut } from 'lucide-react';

const InactivityWarningModal = ({
                                    open,
                                    secondsLeft,
                                    onStaySignedIn,
                                    onSignOutNow,
                                }) => {
    if (!open) return null;

    // Visual countdown — single source of truth is `secondsLeft` from
    // the hook, which is derived from the shared activity timestamp so
    // it stays accurate across tabs.
    const displaySeconds = Math.max(0, secondsLeft);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop — non-dismissible click-through, this prompt matters */}
            <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

            {/* Modal */}
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="inactivity-title"
                aria-describedby="inactivity-desc"
                className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6"
            >
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="text-amber-600" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2
                            id="inactivity-title"
                            className="text-lg font-display font-bold text-gray-900"
                        >
                            Still there?
                        </h2>
                        <p
                            id="inactivity-desc"
                            className="text-sm text-gray-600 mt-1"
                        >
                            You'll be signed out in{' '}
                            <span className="font-semibold text-gray-900 tabular-nums">
                                {displaySeconds}
                            </span>{' '}
                            second{displaySeconds === 1 ? '' : 's'} due to
                            inactivity.
                        </p>
                    </div>
                </div>

                {/* Countdown progress bar — visual reinforcement of the timer */}
                <div className="mt-5">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-amber-500 transition-[width] duration-1000 ease-linear"
                            style={{ width: `${(displaySeconds / 60) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <button
                        type="button"
                        onClick={onSignOutNow}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                        <LogOut size={16} />
                        Sign out now
                    </button>
                    <button
                        type="button"
                        onClick={onStaySignedIn}
                        autoFocus
                        className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] transition-all shadow-sm"
                    >
                        Stay signed in
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InactivityWarningModal;